import React, { useCallback, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';

// Utils
import { clearOrganizationData } from '@repo/front-end-utils';
import { returnToFromState } from '../../components/Auth0/returnTo';

export type FormInputs = {
  email: string;
  password: string;
};

interface Props {}

const LoginPage: React.FC<Props> = () => {
  const { loginWithRedirect, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  // Where a signed-out visit was going, from the protected gate; carried through Auth0 as appState.
  const returnTo = returnToFromState(useLocation().state);
  const hasAttemptedLogin = useRef(false);

  const checkAuth = useCallback(async () => {
    // Wait for Auth0 to finish checking authentication state
    if (isLoading) {
      return;
    }

    // If not authenticated, clear any stale organization data from previous sessions
    // This prevents showing a pre-selected organization when a new user logs in
    if (!isAuthenticated) {
      clearOrganizationData();
    }

    // If already authenticated, try to get token silently
    if (isAuthenticated) {
      try {
        await getAccessTokenSilently();
        // If successful, go where the visit was heading, else the twin page
        navigate(returnTo ?? '/twin', { replace: true });
        return;
      } catch (error) {
        // If silent auth fails, proceed to login
        console.warn('Silent authentication failed, redirecting to login:', error);
        // Clear organization data when silent auth fails
        clearOrganizationData();
      }
    }

    // If not authenticated and we haven't attempted login yet, redirect to Auth0 login
    // Don't use prompt: 'login' to allow silent authentication on refresh
    if (!hasAttemptedLogin.current) {
      hasAttemptedLogin.current = true;
      await loginWithRedirect(returnTo ? { appState: { returnTo } } : undefined);
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, navigate, returnTo]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return null;
};
export default LoginPage;
